// src/components/Phaser/functions/action.ts
import { getEngine } from "./engineManager";

// Helper function to wait for a character to be available
const waitForCharacter = async (maxWaitMs = 1000): Promise<string> => {
  const startTime = Date.now();
  while (Date.now() - startTime < maxWaitMs) {
    const engine = getEngine();
    const characterIds = engine?.getAllCharacterIds?.() || [];
    if (characterIds.length > 0) {
      return characterIds[0];
    }
    await new Promise(resolve => setTimeout(resolve, 50));
  }
  return "";
};

export const sayCharacter = (id: string | number, message: any) => {
  const engine = getEngine();
  const idStr = String(id);
  const char = engine?.getCharacter(idStr);
  if (!char) {
    console.warn(`Character with id ${idStr} not found.`);
    return;
  }
  const messageStr = String(message);
  char.say(messageStr);
  engine?.getEventManager()?.emit("sayPhraseEvent", char.id, messageStr);
};

export const sayObject = (id: string | number, message: any) => {
  const engine = getEngine();
  const idStr = String(id);
  const obj = engine?.getObject?.(idStr) ?? engine?.phaserScene?.getObjectById(idStr);
  if (!obj) return console.warn(`Object with id ${idStr} not found.`);
  const msg = String(message);
  obj.say(msg);
  engine?.getEventManager()?.emit("sayPhraseEvent", obj.id, msg);
};

export async function activateAutoPilot(id: string | number) {
  const engine = getEngine();
  const idStr = String(id);

  // Wait for character to be available in engine
  await waitForCharacter();

  const char = engine?.getCharacter(idStr);
  if (!char) {
    console.warn(`Character with id ${idStr} not found.`);
    return;
  }
  char.startAutoPilot();
}

export async function desactivateAutoPilot(id: string | number) {
  const engine = getEngine();
  const idStr = String(id);

  // Wait for character to be available in engine
  await waitForCharacter();

  const char = engine?.getCharacter(idStr);
  if (!char) {
    console.warn(`Character with id ${idStr} not found.`);
    return;
  }
  char.stopAutoPilot();
}

export function setWin(): void {
  const triggerWin = () => {
    const engine = getEngine();
    if (!engine) {
      return;
    }

    // API officielle du moteur (ajoutée dans GameEngine.ts)
    if (typeof (engine as any).win === "function") {
      (engine as any).win();
      return;
    }

    // Fallback : relayer via l'EventManager (écouté par GameEngine/PhaserScene)
    engine.getEventManager()?.emit("win");
  };

  // S'assure que les personnages sont disponibles avant de déclencher l'animation de victoire
  waitForCharacter(1500).then(() => triggerWin());
}

export function isPicked(id?: string | number): boolean {
  const engine = getEngine();
  const scene = engine?.phaserScene;
  if (!engine || !scene) return false;

  // Vérifie si TOUS les objets ramassables sont déjà portés
  if (id === undefined) {
    const allObjectIds = engine.getAllObjectIds?.() ?? [];
    const pickables = allObjectIds
      .map(oid => engine.getObject?.(oid))
      .filter((o): o is NonNullable<ReturnType<typeof engine.getObject>> => !!o && o.pickable);

    // S'il n'y a aucun objet ramassable, on considère "tout ramassé"
    if (pickables.length === 0) return true;

    return pickables.every(o => o.currentState === "carried");
  }

  // Vérifie l’état d’un objet précis
  const obj = engine.getObject?.(String(id));
  if (!obj) return false;           // pas encore synchronisé ou inexistant
  if (!obj.pickable) return false;  // non ramassable → "pas ramassé"
  return obj.currentState === "carried";
}


export async function move_to(id: string | number, x: number, y: number): Promise<void> {
  const engine = getEngine();
  const idStr = String(id);

  // Wait for character to be available in engine
  await waitForCharacter();

  const char = engine?.getCharacter(idStr);
  if (!char) return console.warn(`move_to(): character with id ${idStr} not found.`);
  char.moveTo(x, y);
}

export async function changeSize(id: string | number, newWidth: number, newHeight: number): Promise<void> {
  const engine = getEngine();
  const idStr = String(id);

  // Wait for character to be available in engine
  await waitForCharacter();

  const char = engine?.getCharacter(idStr);
  if (!char) return console.warn(`changeSize(): character with id ${idStr} not found.`);
  char.changeSize(newWidth, newHeight);
}

export async function moveObjectTo(id: string | number, x: number, y: number): Promise<void> {
  const engine = getEngine();
  const idStr = String(id);
  const obj = engine?.getObject?.(idStr) ?? engine?.phaserScene?.getObjectById(idStr);
  if (!obj) return console.warn(`moveObjectTo(): object with id ${idStr} not found.`);
  obj.moveToforObject(x, y);
}

export async function changeSizeObject(id: string | number, newWidth: number, newHeight: number): Promise<void> {
  const engine = getEngine();
  const idStr = String(id);
  const obj = engine?.getObject?.(idStr) ?? engine?.phaserScene?.getObjectById(idStr);
  if (!obj) return console.warn(`changeSizeObject(): object with id ${idStr} not found.`);
  obj.changeSizeforObject(newWidth, newHeight);
}

export function setGravity(enable: boolean, y?: number): void {
  const engine = getEngine();
  const scene = engine?.phaserScene;
  if (!scene) return console.warn("setGravity(): Phaser scene not found.");
  if (typeof y === "number") scene.setGravity(enable, y);
  else scene.setGravity(enable);
}
